import React, { useState, useEffect, useMemo, useRef } from 'react';
import TagInput from '../common/TagInput';
import { Icon } from '../common/Icons';
import { notesApi, categoriesApi } from '../../api';
import { toast } from 'react-toastify';

const QUICK_TAG_SUGGESTIONS = ['work', 'notes', 'ideas', 'project', 'meeting', 'personal'];

export default function NoteModal({
  note,
  doc,
  initialFolderId,
  initialCategoryId,
  initialMode = 'note',
  onClose,
  onSaved
}) {
  const currentNote = note || doc;
  const isEdit = !!currentNote?.id;

  // Toggle slider between Note and Category creation (only active when creating new)
  const defaultMode = (initialMode === 'folder' || initialMode === 'category') ? 'category' : 'note';
  const [creationMode, setCreationMode] = useState(defaultMode); // 'note' | 'category'

  // Note form state
  const [title, setTitle] = useState(currentNote?.title ?? '');
  const [content, setContent] = useState(currentNote?.content ?? '');
  const [tags, setTags] = useState(currentNote?.tags ? [...currentNote.tags] : []);
  const [categoryId, setCategoryId] = useState(currentNote?.categoryId ?? currentNote?.folderId ?? initialCategoryId ?? initialFolderId ?? '');
  const [categories, setCategories] = useState([]);
  const [showInlineNewCategory, setShowInlineNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [inlineCategoryError, setInlineCategoryError] = useState('');

  // Category creation form state (for slider 'category' mode)
  const [standaloneCategoryName, setStandaloneCategoryName] = useState('');
  const [categoryCreationError, setCategoryCreationError] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);

  // Custom category picker states
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const categoryPickerRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    categoriesApi.list()
      .then(r => setCategories(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setTitle(currentNote?.title ?? '');
    setContent(currentNote?.content ?? '');
    setTags(currentNote?.tags ? [...currentNote.tags] : []);
    setCategoryId(currentNote?.categoryId ?? currentNote?.folderId ?? initialCategoryId ?? initialFolderId ?? '');
    setErrors({});
  }, [currentNote, initialCategoryId, initialFolderId]);

  // Click outside to close category dropdown
  useEffect(() => {
    if (!categoryPickerOpen) return;
    const handleClickOutside = (e) => {
      if (categoryPickerRef.current && !categoryPickerRef.current.contains(e.target)) {
        setCategoryPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [categoryPickerOpen]);

  // Keyboard shortcut: Cmd+Enter / Ctrl+Enter to save, Esc to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (creationMode === 'note') {
          handleSave();
        } else {
          handleCreateCategorySubmit();
        }
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  const wordCount = useMemo(() => {
    return content.trim() ? content.trim().split(/\s+/).length : 0;
  }, [content]);

  const readTime = useMemo(() => {
    if (wordCount === 0) return '0 min';
    const m = Math.ceil(wordCount / 180);
    return `${m} min`;
  }, [wordCount]);

  const filteredCategories = useMemo(() => {
    if (!categorySearchQuery.trim()) return categories;
    const q = categorySearchQuery.toLowerCase();
    return categories.filter(c => c.name.toLowerCase().includes(q));
  }, [categories, categorySearchQuery]);

  const selectedCategoryName = useMemo(() => {
    if (!categoryId) return 'General';
    const found = categories.find(c => String(c.id) === String(categoryId));
    return found ? found.name : 'General';
  }, [categoryId, categories]);

  const validate = () => {
    const e = {};
    if (!title.trim())   e.title   = 'Note title is required';
    if (!content.trim()) e.content = 'Note content cannot be empty';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    // Check duplicate if user typed an inline category name
    if (showInlineNewCategory && newCategoryName.trim()) {
      const exists = categories.some(c => c.name.trim().toLowerCase() === newCategoryName.trim().toLowerCase());
      if (exists) {
        setInlineCategoryError(`Category "${newCategoryName.trim()}" already exists.`);
        return;
      }
    }

    setSaving(true);
    try {
      let effectiveCategoryId = categoryId ? Number(categoryId) : undefined;

      // If user typed a new category name inline, auto-create it now
      if (showInlineNewCategory && newCategoryName.trim()) {
        try {
          const { data: createdCategory } = await categoriesApi.create({ name: newCategoryName.trim() });
          effectiveCategoryId = createdCategory.id;
          setCategories(prev => [...prev, createdCategory]);
        } catch (categoryErr) {
          const msg = categoryErr.response?.data?.message || 'Failed to create category';
          setInlineCategoryError(msg);
          setSaving(false);
          return;
        }
      }

      if (isEdit) {
        const payload = {
          title: title.trim(),
          content: content.trim(),
          tags: tags,
        };
        if (effectiveCategoryId !== undefined) {
          payload.categoryId = effectiveCategoryId;
          payload.folderId = effectiveCategoryId;
        }

        const { data } = await notesApi.update(currentNote.id, payload);
        toast.success('Note updated');
        onSaved?.(data);
        window.dispatchEvent(new CustomEvent('memora:note-saved', { detail: data }));
        window.dispatchEvent(new CustomEvent('memora:doc-created', { detail: data }));
      } else {
        const payload = {
          title: title.trim(),
          content: content.trim(),
          tags,
          categoryId: effectiveCategoryId,
          folderId: effectiveCategoryId,
        };
        const { data } = await notesApi.create(payload);
        toast.success('Note created');
        onSaved?.(data);
        window.dispatchEvent(new CustomEvent('memora:note-saved', { detail: data }));
        window.dispatchEvent(new CustomEvent('memora:doc-created', { detail: data }));
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message ?? 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  // Category creation submission (slider mode)
  const handleCreateCategorySubmit = async (e) => {
    e?.preventDefault();
    const name = standaloneCategoryName.trim();
    if (!name) return;

    const exists = categories.some(c => c.name.trim().toLowerCase() === name.toLowerCase());
    if (exists) {
      setCategoryCreationError(`Category "${name}" already exists.`);
      return;
    }

    setSavingCategory(true);
    try {
      const { data: createdCategory } = await categoriesApi.create({ name });
      toast.success(`Category "${createdCategory.name}" created`);
      window.dispatchEvent(new CustomEvent('memora:category-saved', { detail: createdCategory }));
      window.dispatchEvent(new CustomEvent('memora:folder-saved', { detail: createdCategory }));
      onSaved?.(createdCategory);
      onClose();
    } catch (err) {
      setCategoryCreationError(err.response?.data?.message || 'Failed to create category');
    } finally {
      setSavingCategory(false);
    }
  };

  const addSuggestedTag = (tag) => {
    if (!tags.includes(tag)) {
      setTags([...tags, tag]);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-sheet-box search-modal-centered create-note-modal-box" role="dialog" aria-modal="true">
        {/* Modal Header */}
        <div className="search-modal-header">
          {!isEdit ? (
            <div className="search-slider-toggle">
              <div className={`search-slider-pill ${creationMode === 'note' ? 'notes' : 'folders'}`} />
              <button
                type="button"
                className={`search-slider-btn ${creationMode === 'note' ? 'active' : ''}`}
                onClick={() => {
                  setCreationMode('note');
                  setCategoryCreationError('');
                }}
              >
                <Icon name="note" size={15} />
                <span>Create Note</span>
              </button>
              <button
                type="button"
                className={`search-slider-btn ${creationMode === 'category' ? 'active' : ''}`}
                onClick={() => {
                  setCreationMode('category');
                  setCategoryCreationError('');
                }}
              >
                <Icon name="category" size={15} />
                <span>Create Category</span>
              </button>
            </div>
          ) : (
            <div className="modal-header-title-group" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <Icon name="edit" size={18} />
              <h2 className="modal-title" style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Edit Note</h2>
            </div>
          )}

          <button
            type="button"
            className="btn-icon search-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        {/* MODE: CREATE NOTE */}
        {creationMode === 'note' ? (
          <>
            <div className="modal-body">
              {/* Note Title */}
              <div className="form-group">
                <label className="form-label" htmlFor="note-title-input">
                  Title
                </label>
                <input
                  id="note-title-input"
                  className={`form-input ${errors.title ? 'error' : ''}`}
                  placeholder="Note title..."
                  value={title}
                  onChange={e => {
                    setTitle(e.target.value);
                    if (errors.title) setErrors(prev => ({ ...prev, title: null }));
                  }}
                  autoFocus
                />
                {errors.title && <div className="form-field-error">{errors.title}</div>}
              </div>

              {/* Category Selector */}
              <div className="form-group">
                <div className="form-label-row">
                  <label className="form-label">
                    Category
                  </label>
                  {!showInlineNewCategory && (
                    <button
                      type="button"
                      className="btn-new-folder-action"
                      onClick={() => {
                        setShowInlineNewCategory(true);
                        setInlineCategoryError('');
                        setCategoryPickerOpen(false);
                      }}
                      title="Create new category"
                    >
                      <Icon name="plus" size={13} />
                      <span>New Category</span>
                    </button>
                  )}
                </div>

                {showInlineNewCategory ? (
                  <div style={{ marginTop: '0.65rem' }}>
                    <div className="inline-new-folder-bar">
                      <div className="input-with-icon-wrap" style={{ flex: 1 }}>
                        <Icon name="category" size={16} className="input-leading-icon" />
                        <input
                          className={`form-input with-leading-icon ${inlineCategoryError ? 'error' : ''}`}
                          placeholder="Enter category name (auto-created on save)..."
                          value={newCategoryName}
                          onChange={e => {
                            setNewCategoryName(e.target.value);
                            setInlineCategoryError('');
                          }}
                          autoFocus
                        />
                      </div>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => {
                          setShowInlineNewCategory(false);
                          setNewCategoryName('');
                          setInlineCategoryError('');
                        }}
                        title="Cancel new category"
                        aria-label="Cancel new category"
                      >
                        <Icon name="close" size={16} />
                      </button>
                    </div>
                    {inlineCategoryError && (
                      <div className="form-field-error" style={{ marginTop: '0.45rem' }}>
                        {inlineCategoryError}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="custom-folder-picker-wrapper" ref={categoryPickerRef} style={{ marginTop: '0.65rem' }}>
                    <button
                      type="button"
                      className={`custom-folder-picker-trigger ${categoryPickerOpen ? 'open' : ''}`}
                      onClick={() => setCategoryPickerOpen(prev => !prev)}
                    >
                      <div className="folder-picker-current">
                        <Icon name="category" size={16} className="folder-picker-icon" />
                        <span>{selectedCategoryName}</span>
                        {(!categoryId || selectedCategoryName === 'General') && (
                          <span className="category-default-chip-grey">Default</span>
                        )}
                      </div>
                      <Icon name={categoryPickerOpen ? 'chevron-up' : 'chevron-down'} size={15} className="folder-picker-arrow" />
                    </button>

                    {categoryPickerOpen && (
                      <div className="custom-folder-picker-menu">
                        <div className="folder-picker-search-bar">
                          <Icon name="search" size={14} className="folder-picker-search-icon" />
                          <input
                            className="folder-picker-search-input"
                            placeholder="Search categories..."
                            value={categorySearchQuery}
                            onChange={e => setCategorySearchQuery(e.target.value)}
                            autoFocus
                          />
                          {categorySearchQuery && (
                            <button
                              type="button"
                              className="folder-picker-clear-search"
                              onClick={() => setCategorySearchQuery('')}
                            >
                              <Icon name="close" size={12} />
                            </button>
                          )}
                        </div>

                        <div className="folder-picker-options-list">
                          {/* Default: General */}
                          <div
                            className={`folder-picker-option ${!categoryId ? 'selected' : ''}`}
                            onClick={() => {
                              setCategoryId('');
                              setCategoryPickerOpen(false);
                            }}
                          >
                            <div className="folder-picker-option-left">
                              <Icon name="category" size={15} />
                              <span className="folder-picker-option-name">General</span>
                              <span className="category-default-chip-grey">Default</span>
                            </div>
                            {!categoryId && <Icon name="check" size={15} className="folder-picker-check" />}
                          </div>

                          {/* Custom Categories */}
                          {filteredCategories.map(c => {
                            if (c.name === 'General' || c.name === 'My Folder') return null;
                            const isSelected = String(categoryId) === String(c.id);
                            return (
                              <div
                                key={c.id}
                                className={`folder-picker-option ${isSelected ? 'selected' : ''}`}
                                onClick={() => {
                                  setCategoryId(c.id);
                                  setCategoryPickerOpen(false);
                                }}
                              >
                                <div className="folder-picker-option-left">
                                  <Icon name="category" size={15} />
                                  <span className="folder-picker-option-name">{c.name}</span>
                                </div>
                                {isSelected && <Icon name="check" size={15} className="folder-picker-check" />}
                              </div>
                            );
                          })}

                          {filteredCategories.filter(c => c.name !== 'General' && c.name !== 'My Folder').length === 0 && categorySearchQuery && (
                            <div className="folder-picker-no-results">
                              No categories found matching "{categorySearchQuery}"
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Note Content */}
              <div className="form-group">
                <div className="form-label-row">
                  <label className="form-label" htmlFor="note-content-input">
                    Content
                  </label>
                  <div className="content-meta-counter">
                    <span>{wordCount} words</span>
                    <span className="dot">·</span>
                    <span>{content.length} chars</span>
                    <span className="dot">·</span>
                    <span>{readTime}</span>
                  </div>
                </div>
                <textarea
                  id="note-content-input"
                  className={`form-input form-textarea ${errors.content ? 'error' : ''}`}
                  placeholder="Take a note..."
                  rows={8}
                  value={content}
                  onChange={e => {
                    setContent(e.target.value);
                    if (errors.content) setErrors(prev => ({ ...prev, content: null }));
                  }}
                />
                {errors.content && <div className="form-field-error">{errors.content}</div>}
              </div>

              {/* Hashtags Section */}
              <div className="form-group">
                <label className="form-label">
                  Hashtags
                </label>
                <TagInput
                  tags={tags}
                  onChange={setTags}
                  placeholder="e.g. work, personal, ..., then press enter or tab or space"
                />

                {/* Quick suggestions */}
                <div className="tag-suggestions-row">
                  <span className="suggestions-label">Suggestions:</span>
                  {QUICK_TAG_SUGGESTIONS.map(st => (
                    <button
                      key={st}
                      type="button"
                      className={`tag-suggestion-chip ${tags.includes(st) ? 'selected' : ''}`}
                      onClick={() => addSuggestedTag(st)}
                    >
                      +{st}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <div />

              <div className="modal-footer-btns">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary-custom"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? <div className="spinner sm" /> : <Icon name="check" size={16} />}
                  <span>Save</span>
                </button>
              </div>
            </div>
          </>
        ) : (
          /* MODE: CREATE CATEGORY */
          <form onSubmit={handleCreateCategorySubmit}>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label" htmlFor="standalone-category-input">
                  Name
                </label>
                <input
                  id="standalone-category-input"
                  className={`form-input ${categoryCreationError ? 'error' : ''}`}
                  placeholder="e.g. Work, Ideas, Travel, Research..."
                  value={standaloneCategoryName}
                  onChange={e => {
                    setStandaloneCategoryName(e.target.value);
                    setCategoryCreationError('');
                  }}
                  autoFocus
                />
                {categoryCreationError && (
                  <div className="form-field-error" style={{ marginTop: '0.45rem' }}>
                    {categoryCreationError}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <div />
              <div className="modal-footer-btns">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={onClose}
                  disabled={savingCategory}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary-custom"
                  disabled={!standaloneCategoryName.trim() || !!categoryCreationError || savingCategory}
                >
                  {savingCategory ? <div className="spinner sm" /> : <Icon name="check" size={16} />}
                  <span>{savingCategory ? 'Creating...' : 'Create'}</span>
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
